import React, { createContext, useContext, useState, useCallback } from "react";
import {
  getFinancialPlanData,
  getFinPlanPerformerTypes,
  submitFinancialPlanForApproval,
  calculateFinancialPlan,
  getFinancialPlanSectionDetails,
  calculateFinancialPlanSection,
} from "../../../../api/projectApi";
import { getAPIURL } from "../../../../api/config";

/**
 * Контекст для управления состоянием финансового плана
 *
 * Аналогично EstimateContext, но для финансового плана
 * Использует реальный API для получения данных
 *
 * ОБНОВЛЕНО: Добавлены новые функции для работы с API:
 * - loadFinancialPlanData(projectId) - загрузка всех данных финансового плана с сервера
 * - loadSectionDetails(projectId, sectionId) - загрузка данных конкретной секции
 * - performCalculation(projectId) - калькуляция с обновлением контекста
 * - performSave(projectId) - сохранение с обновлением контекста
 */

/**
 * Преобразует данные из API в формат, ожидаемый интерфейсом
 * @param {Object} apiData - Данные из API
 * @returns {Object} Преобразованные данные для интерфейса
 */
const transformApiDataToUIFormat = (apiData) => {
  if (!apiData) return null;

  // Преобразуем данные construction_design в формат для рабочего проектирования
  const workingDesignSections = (apiData.construction_design || []).map(
    (section) => ({
      id: section.section.id,
      name: section.section.name,
      labor_days: section.day_efforts.direct,
      bonus_days: section.day_efforts.bonus,
      direct_costs: section.money_expenditures.direct,
      bonus_costs: section.money_expenditures.bonus,
      total_costs: section.price_with_payroll_taxes,
      payroll_taxes: section.payroll_taxes,
      base_price_per_m2: section.base_price_per_m2,
      expenditures_percent: section.expenditures_percent,
      different_in_expenditures: section.different_in_expenditures,
    })
  );

  // Преобразуем BIM coordination
  const bimSections = (apiData.bim_coordination?.roles || []).map((role) => ({
    id: role.role,
    name: role.role,
    labor_days: role.day_efforts.direct,
    bonus_days: role.day_efforts.bonus,
    direct_costs: role.money_expenditures.direct,
    bonus_costs: role.money_expenditures.bonus,
    performer: role.performer,
  }));

  // Преобразуем Quality control
  const qualitySections = (apiData.quality_control?.roles || []).map(
    (role) => ({
      id: role.role,
      name: role.role,
      labor_days: role.day_efforts.direct,
      bonus_days: role.day_efforts.bonus,
      direct_costs: role.money_expenditures.direct,
      bonus_costs: role.money_expenditures.bonus,
      performer: role.performer,
    })
  );

  // Преобразуем Project delivery
  const deliverySections = (apiData.project_delivery?.roles || []).map(
    (role) => ({
      id: role.role,
      name: role.role,
      labor_days: role.day_efforts.direct,
      bonus_days: role.day_efforts.bonus,
      direct_costs: role.money_expenditures.direct,
      bonus_costs: role.money_expenditures.bonus,
      performer: role.performer,
    })
  );

  // Преобразуем Project management
  const managementSections = (apiData.project_management?.roles || []).map(
    (role) => ({
      id: role.role,
      name: role.role,
      labor_days: role.day_efforts.direct,
      bonus_days: role.day_efforts.bonus,
      direct_costs: role.money_expenditures.direct,
      bonus_costs: role.money_expenditures.bonus,
      performer: role.performer,
      coefficient: role.coefficient,
    })
  );

  return {
    headerData: {
      contract_value: apiData.contract_value || 0,
      contract_value_before_nds: apiData.contract_value_before_nds || 0,
      contract_value_construction: apiData.contract_value_construction || 0,
      contract_value_construction_percent:
        apiData.contract_value_construction_percent || 0,
      cost_price: apiData.cost_price || 0,
      difference: apiData.difference || 0,
      nds_value: apiData.nds_value || 0,
      total_sum: apiData.contract_value || 0,
      total_sum_percent: 100,
      indirect_expenditures: apiData.indirect_expenditures || 0,
      indirect_expenditures_percent: apiData.indirect_expenditures_percent || 0,
      cost_price_percent: apiData.cost_price_percent || 0,
      pure_income: apiData.pure_income || 0,
      pure_income_percent: apiData.pure_income_percent || 0,
      total_area: apiData.total_area || 0,
    },
    sections: {
      working_design: {
        sections: workingDesignSections,
        total_costs:
          apiData.total_construction_design?.price_with_payroll_taxes || 0,
        direct_costs:
          apiData.total_construction_design?.money_expenditures?.direct || 0,
        bonus_fund:
          apiData.total_construction_design?.money_expenditures?.bonus || 0,
        bonus_percent: apiData.bonus_percent_construction_design || 0,
        include_bonuses: true,
        total_day_efforts: apiData.total_construction_design?.day_efforts || {
          direct: 0,
          bonus: 0,
          total: 0,
        },
      },
      bim_coordination: {
        sections: bimSections,
        total_costs: apiData.bim_coordination?.total || 0,
        direct_costs:
          apiData.bim_coordination?.total_money_expenditures?.direct || 0,
        bonus_fund:
          apiData.bim_coordination?.total_money_expenditures?.bonus || 0,
        bonus_percent: apiData.bim_coordination?.direct_bonus_percent || 0,
        include_bonuses: apiData.bim_coordination?.include_bonuses ?? true,
        bim_percent: apiData.bim_coordination?.quality_control_percent || 0,
        total_day_efforts: apiData.bim_coordination?.total_day_efforts || {
          direct: 0,
          bonus: 0,
        },
      },
      quality_control: {
        sections: qualitySections,
        total_costs: apiData.quality_control?.total || 0,
        direct_costs:
          apiData.quality_control?.total_money_expenditures?.direct || 0,
        bonus_fund:
          apiData.quality_control?.total_money_expenditures?.bonus || 0,
        bonus_percent: apiData.quality_control?.direct_bonus_percent || 0,
        include_bonuses: apiData.quality_control?.include_bonuses ?? true,
        quality_percent: apiData.quality_control?.quality_control_percent || 0,
        normative_control_efforts:
          apiData.quality_control?.roles?.find(
            (role) => role.role === "Норм Контроль"
          )?.day_efforts?.direct || 0,
        total_day_efforts: apiData.quality_control?.total_day_efforts || {
          direct: 0,
          bonus: 0,
        },
      },
      project_delivery: {
        sections: deliverySections,
        total_costs: apiData.project_delivery?.total || 0,
        direct_costs:
          apiData.project_delivery?.total_money_expenditures?.direct || 0,
        bonus_fund:
          apiData.project_delivery?.total_money_expenditures?.bonus || 0,
        bonus_percent: apiData.project_delivery?.direct_bonus_percent || 0,
        include_bonuses: apiData.project_delivery?.include_bonuses ?? true,
        delivery_percent:
          apiData.project_delivery?.quality_control_percent || 0,
        total_day_efforts: apiData.project_delivery?.total_day_efforts || {
          direct: 0,
          bonus: 0,
        },
      },
      project_management: {
        sections: managementSections,
        total_costs: apiData.project_management?.total || 0,
        direct_costs:
          apiData.project_management?.total_money_expenditures?.direct || 0,
        bonus_fund:
          apiData.project_management?.total_money_expenditures?.bonus || 0,
        bonus_percent: apiData.project_management?.direct_bonus_percent || 0,
        include_bonuses: apiData.project_management?.include_bonuses ?? true,
        management_percent:
          apiData.project_management?.quality_control_percent || 0,
        administration_efforts:
          apiData.project_management?.roles?.find(
            (role) => role.role === "Администрирование Проекта"
          )?.day_efforts?.direct || 0,
        total_day_efforts: apiData.project_management?.total_day_efforts || {
          direct: 0,
          bonus: 0,
        },
      },
    },
    // Сохраняем исходные данные API для дополнительных операций
    rawApiData: apiData,
  };
};

// Создать контекст для работы с финансовым планом.
// Данные секций берутся из основного ответа (construction_design_detailed), без отдельных запросов.
export const createFinancialPlanContext = async (projectId) => {
  try {
    const financialPlanData = await getFinancialPlanData(projectId);

    // Собираем детали секций из основного ответа (construction_design_detailed).
    // API отдаёт day_efforts/bonus_day_efforts/money в costs_per_block — нормализуем на верхний уровень блока для UI.
    const sectionsData = {};
    const detailed = financialPlanData.construction_design_detailed || [];
    for (const section of financialPlanData.construction_design || []) {
      const sectionId = section.section?.id;
      if (sectionId == null) continue;
      const det = detailed.find((d) => d.section_id === sectionId);
      const rawBlocks = det?.block_expenditures ?? [];
      const block_expenditures = rawBlocks.map((b) => {
        const cp = b.costs_per_block || {};
        return {
          ...b,
          day_efforts: b.day_efforts ?? cp.day_efforts ?? 0,
          bonus_day_efforts: b.bonus_day_efforts ?? cp.bonus_day_efforts ?? 0,
          money_expenditures: b.money_expenditures ?? cp.money_expenditures ?? 0,
          bonus_money_expenditures:
            b.bonus_money_expenditures ?? cp.bonus_money_expenditures ?? 0,
        };
      });
      sectionsData[sectionId] = {
        block_expenditures,
        bonus_percent: det?.bonus_percent ?? 0,
        threshold_percent: section.expenditures_percent?.threshold ?? 0,
        total_day_efforts: det?.total_day_efforts ?? 0,
        average_salary: 0,
      };
    }

    return {
      financialPlan: financialPlanData,
      sections: sectionsData,
      projectId,
    };
  } catch (error) {
    console.error("Error creating financial plan context:", error);
    throw error;
  }
};

// Получить контекст для конкретной секции
export const getFinancialPlanSectionContext = async (projectId, sectionId) => {
  try {
    const sectionData = await getFinancialPlanSectionDetails(
      projectId,
      sectionId
    );
    return {
      section: sectionData,
      projectId,
      sectionId,
    };
  } catch (error) {
    console.error(
      `Error getting financial plan section context for ${sectionId}:`,
      error
    );
    throw error;
  }
};

// Функция для обновления контекста финансового плана с результатами калькуляции
const updateFinancialPlanContextWithCalculationResults = (
  context,
  calculationResults
) => {
  if (!calculationResults) return context;

  // Обновляем данные финансового плана результатами калькуляции
  const updatedFinancialPlan = {
    ...context.financialPlan,
    ...calculationResults,
  };

  // Обновляем construction_design с новыми данными из construction_design_detailed
  if (calculationResults.construction_design_detailed) {
    const updatedConstructionDesign =
      updatedFinancialPlan.construction_design?.map((section) => {
        const detailedSection =
          calculationResults.construction_design_detailed.find(
            (det) => det.section_id === section.section.id
          );

        if (detailedSection) {
          // Обновляем day_efforts на основе данных из block_expenditures
          const totalDirectEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.day_efforts || 0),
              0
            ) || 0;
          const totalBonusEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.bonus_day_efforts || 0),
              0
            ) || 0;

          return {
            ...section,
            day_efforts: {
              direct: totalDirectEfforts,
              bonus: totalBonusEfforts,
              total: totalDirectEfforts + totalBonusEfforts,
            },
          };
        }

        return section;
      }) || [];

    updatedFinancialPlan.construction_design = updatedConstructionDesign;
  }

  // Обновляем детали секций с новыми данными из construction_design_detailed
  const updatedSections = { ...context.sections };

  if (calculationResults.construction_design_detailed) {
    calculationResults.construction_design_detailed.forEach((sectionDetail) => {
      const sectionId = sectionDetail.section_id;
      if (updatedSections[sectionId]) {
        // Обновляем существующие данные секции
        updatedSections[sectionId] = {
          ...updatedSections[sectionId],
          bonus_percent: sectionDetail.bonus_percent || 0,
          average_salary: sectionDetail.average_salary || 0,
          block_expenditures:
            sectionDetail.block_expenditures?.map((block) => ({
              block: {
                id: block.block?.id || block.block?.tei_id,
                tei_id: block.block?.tei_id,
                name: block.block?.name || `Block ${block.block?.tei_id}`,
                // Поддерживаем новую структуру с массивом performers и старую с одним performer
                performers:
                  block.block?.performers ||
                  (block.block?.performer ? [block.block.performer] : []),
                performer: block.block?.performer, // Оставляем для обратной совместимости
              },
              floor_count: block.floor_count || 0,
              day_efforts: block.day_efforts || 0,
              bonus_day_efforts: block.bonus_day_efforts || 0,
              money_expenditures: block.money_expenditures || 0,
              bonus_money_expenditures: block.bonus_money_expenditures || 0,
            })) || [],
        };
      } else {
        // Создаем новые данные секции, если их не было
        updatedSections[sectionId] = {
          bonus_percent: sectionDetail.bonus_percent || 0,
          average_salary: sectionDetail.average_salary || 0,
          block_expenditures:
            sectionDetail.block_expenditures?.map((block) => ({
              block: {
                id: block.block?.id || block.block?.tei_id,
                tei_id: block.block?.tei_id,
                name: block.block?.name || `Block ${block.block?.tei_id}`,
                // Поддерживаем новую структуру с массивом performers и старую с одним performer
                performers:
                  block.block?.performers ||
                  (block.block?.performer ? [block.block.performer] : []),
                performer: block.block?.performer, // Оставляем для обратной совместимости
              },
              floor_count: block.floor_count || 0,
              day_efforts: block.day_efforts || 0,
              bonus_day_efforts: block.bonus_day_efforts || 0,
              money_expenditures: block.money_expenditures || 0,
              bonus_money_expenditures: block.bonus_money_expenditures || 0,
            })) || [],
        };
      }
    });
  }

  return {
    financialPlan: updatedFinancialPlan,
    sections: updatedSections,
  };
};

const FinancialPlanStateContext = createContext();

export const useFinancialPlanState = () => {
  const context = useContext(FinancialPlanStateContext);
  if (!context) {
    throw new Error(
      "useFinancialPlanState должен использоваться внутри FinancialPlanStateProvider"
    );
  }
  return context;
};

export const FinancialPlanStateProvider = ({ children }) => {
  // Состояние загрузки
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Детальные данные секций (аналогично EstimateContext)
  const [sectionDetails, setSectionDetails] = useState({});

  // Выбранные исполнители
  const [selectedSpecialists, setSelectedSpecialists] = useState({});

  // Трудозатраты по каждому специалисту (ключ: "sectionName-blockName-specIdx")
  const [specialistEfforts, setSpecialistEffortsState] = useState({});

  // Типы исполнителей из API (fin_plan/performer_types)
  const [performerTypes, setPerformerTypes] = useState([]);

  // Основные данные финансового плана
  const [financialPlanData, setFinancialPlanData] = useState({
    // Данные шапки
    headerData: {
      contract_value: 0,
      contract_value_before_nds: 0,
      contract_value_construction: 0,
      contract_value_construction_percent: 0,
      cost_price: 0,
      difference: 0,
      nds_value: 0,
      total_sum: 0,
      total_sum_percent: 0,
      indirect_expenditures: 0,
      indirect_expenditures_percent: 0,
      cost_price_percent: 0,
      pure_income: 0,
      pure_income_percent: 0,
    },

    // Данные по разделам
    sections: {
      working_design: {
        sections: [],
        total_costs: 0,
        direct_costs: 0,
        bonus_fund: 0,
        bonus_percent: 30,
        include_bonuses: true,
      },
      bim_coordination: {
        sections: [],
        total_costs: 0,
        direct_costs: 0,
        bonus_fund: 0,
        bonus_percent: 65,
        include_bonuses: true,
        bim_percent: 4,
      },
      quality_control: {
        sections: [],
        total_costs: 0,
        direct_costs: 0,
        bonus_fund: 0,
        bonus_percent: 65,
        include_bonuses: true,
        quality_percent: 5,
      },
      project_delivery: {
        sections: [],
        total_costs: 0,
        direct_costs: 0,
        bonus_fund: 0,
        bonus_percent: 50,
        include_bonuses: true,
        delivery_percent: 5,
      },
      project_management: {
        sections: [],
        total_costs: 0,
        direct_costs: 0,
        bonus_fund: 0,
        bonus_percent: 50,
        include_bonuses: true,
        management_percent: 7,
      },
    },
  });

  // Имена типов исполнителей по внутреннему типу специалиста (маппинг на ответ API)
  const SPECIALIST_TO_PERFORMER_TYPE_NAMES = {
    "employee_fulltime": "Штатный сотрудник",
    "employee_contract-working": "Сотрудник на подряд (в рабочее время)",
    "employee_contract-nonworking": "Сотрудник на подряд (в нерабочее время)",
    "individual": "Физическое лицо",
    "legal": "Юридическое лицо",
  };

  const buildPerformerFromSpecialist = useCallback(
    (specialist) => {
      if (!specialist) {
        return {
          employee: { id: 0, name: "" },
          performer_type: { id: 0, name: "" },
          entity_average_salary: 0,
          entity_name: "",
          day_efforts: 0,
        };
      }

      let typeName = null;
      let entityAverageSalary = 0;

      if (specialist.type === "employee") {
        if (specialist.employeeType === "fulltime") {
          typeName = SPECIALIST_TO_PERFORMER_TYPE_NAMES.employee_fulltime;
          entityAverageSalary = specialist.salary || 0;
        } else if (specialist.employeeType === "contract-working") {
          typeName =
            SPECIALIST_TO_PERFORMER_TYPE_NAMES["employee_contract-working"];
          entityAverageSalary = specialist.salary || 0;
        } else if (specialist.employeeType === "contract-nonworking") {
          typeName =
            SPECIALIST_TO_PERFORMER_TYPE_NAMES["employee_contract-nonworking"];
          entityAverageSalary = specialist.rate
            ? parseFloat(specialist.rate.replace(/[₸\s]/g, "")) || 0
            : 0;
        }
      } else if (specialist.type === "individual") {
        typeName = SPECIALIST_TO_PERFORMER_TYPE_NAMES.individual;
        entityAverageSalary = specialist.rate
          ? parseFloat(specialist.rate.replace(/[₸\s]/g, "")) || 0
          : 0;
      } else if (specialist.type === "legal") {
        typeName = SPECIALIST_TO_PERFORMER_TYPE_NAMES.legal;
        entityAverageSalary = specialist.rate
          ? parseFloat(specialist.rate.replace(/[₸\s]/g, "")) || 0
          : 0;
      }

      const resolvedType =
        typeName && Array.isArray(performerTypes) && performerTypes.length > 0
          ? performerTypes.find((t) => t.name === typeName) || { id: 0, name: typeName }
          : typeName
            ? { id: 0, name: typeName }
            : performerTypes.find(
                (t) => t.name === SPECIALIST_TO_PERFORMER_TYPE_NAMES.employee_fulltime
              ) || { id: 0, name: "Штатный сотрудник" };

      // Для сотрудника — employeeId, для субподрядчика (контрагент) — counterpartyId
      const entityId =
        specialist.type === "employee"
          ? specialist.employeeId || 0
          : specialist.counterpartyId ?? specialist.employeeId ?? 0;

      return {
        employee: {
          id: entityId,
          name: specialist.name || "",
        },
        performer_type: resolvedType,
        entity_average_salary: entityAverageSalary,
        entity_name: specialist.fullName || specialist.name || "",
        day_efforts: specialist.day_efforts ?? 0,
      };
    },
    [performerTypes]
  );

  // Обратная функция: преобразование performer обратно в specialist
  const buildSpecialistFromPerformer = useCallback((performer, blockName) => {
    console.log(`    [buildSpecialistFromPerformer] Входные данные:`, {
      performer,
      blockName,
      hasPerformer: !!performer,
      hasEmployee: !!performer?.employee,
      employeeId: performer?.employee?.id,
      performerTypeName: performer?.performer_type?.name,
    });

    if (!performer || !performer.employee?.id) {
      console.log(
        `    [buildSpecialistFromPerformer] ❌ Нет performer или employee.id`
      );
      return null;
    }

    // ВАЖНО: Определяем тип по ИМЕНИ, а не по ID, так как сервер возвращает динамические ID
    const performerTypeName = performer.performer_type?.name || "";
    const isCounterparty =
      performerTypeName === "Физическое лицо" || performerTypeName === "Юридическое лицо";
    let specialistData = {
      employeeId: performer.employee.id,
      counterpartyId: isCounterparty ? performer.employee.id : undefined,
      name: performer.employee.name,
      fullName: performer.entity_name,
    };

    console.log(
      `    [buildSpecialistFromPerformer] performerTypeName: "${performerTypeName}"`
    );

    // Определяем тип специалиста на основе performer_type.name
    if (performerTypeName === "Штатный сотрудник") {
      specialistData.type = "employee";
      specialistData.employeeType = "fulltime";
      specialistData.salary = performer.entity_average_salary;
      console.log(`    [buildSpecialistFromPerformer] ✓ Штатный сотрудник`);
    } else if (performerTypeName === "Сотрудник на подряд (в рабочее время)") {
      specialistData.type = "employee";
      specialistData.employeeType = "contract-working";
      specialistData.salary = performer.entity_average_salary;
      console.log(
        `    [buildSpecialistFromPerformer] ✓ Подряд (рабочее время)`
      );
    } else if (
      performerTypeName === "Сотрудник на подряд (в нерабочее время)"
    ) {
      specialistData.type = "employee";
      specialistData.employeeType = "contract-nonworking";
      specialistData.rate = `₸ ${performer.entity_average_salary.toLocaleString()}`;
      console.log(
        `    [buildSpecialistFromPerformer] ✓ Подряд (нерабочее время)`
      );
    } else if (performerTypeName === "Физическое лицо") {
      specialistData.type = "individual";
      specialistData.rate = `₸ ${performer.entity_average_salary.toLocaleString()}`;
      console.log(`    [buildSpecialistFromPerformer] ✓ Физ. лицо`);
    } else if (performerTypeName === "Юридическое лицо") {
      specialistData.type = "legal";
      specialistData.rate = `₸ ${performer.entity_average_salary.toLocaleString()}`;
      console.log(`    [buildSpecialistFromPerformer] ✓ Юр. лицо`);
    } else {
      console.log(
        `    [buildSpecialistFromPerformer] ❌ Неизвестный performerTypeName: "${performerTypeName}"`
      );
      return null;
    }

    console.log(
      `    [buildSpecialistFromPerformer] ✓ Результат:`,
      specialistData
    );
    return specialistData;
  }, []);

  // Функция загрузки данных финансового плана
  const loadFinancialPlanData = useCallback(
    async (projectId) => {
      setIsLoading(true);
      setError(null);

      try {
        // Загружаем типы исполнителей и контекст параллельно
        const [performerTypesList, context] = await Promise.all([
          getFinPlanPerformerTypes().catch(() => []),
          createFinancialPlanContext(projectId),
        ]);
        setPerformerTypes(Array.isArray(performerTypesList) ? performerTypesList : []);

        // Преобразуем данные API в формат интерфейса
        const transformedData = transformApiDataToUIFormat(
          context.financialPlan
        );

        if (transformedData) {
          setFinancialPlanData(transformedData);
          setSectionDetails(context.sections);

          // Восстанавливаем selectedSpecialists из загруженных данных
          console.log("=== ВОССТАНОВЛЕНИЕ СПЕЦИАЛИСТОВ ===");
          console.log("context.financialPlan:", context.financialPlan);
          console.log("context.sections:", context.sections);

          const restoredSpecialists = {};
          const restoredEfforts = {};

          // Проходим по construction_design секциям
          if (context.financialPlan?.construction_design) {
            context.financialPlan.construction_design.forEach((section) => {
              const sectionId = section.section.id;
              const sectionName = section.section.name;
              const sectionDetailsData = context.sections[sectionId];

              console.log(
                `Секция "${sectionName}" (ID: ${sectionId}):`,
                sectionDetailsData
              );

              if (sectionDetailsData?.block_expenditures) {
                sectionDetailsData.block_expenditures.forEach((block, idx) => {
                  const blockName = block.block?.name || "Блок";

                  // Поддерживаем новую структуру с массивом performers и старую с одним performer
                  let performers = [];
                  if (
                    block.block?.performers &&
                    Array.isArray(block.block.performers)
                  ) {
                    // Новая структура API с массивом performers
                    performers = block.block.performers;
                  } else if (block.block?.performer) {
                    // Старая структура API с одним performer
                    performers = [block.block.performer];
                  }

                  console.log(`  Блок ${idx} "${blockName}":`, {
                    block: block.block,
                    performers: performers,
                    performersCount: performers.length,
                  });

                  // Восстанавливаем всех performers для данного блока и их трудозатраты
                  if (performers.length > 0) {
                    const specialists = [];
                    performers.forEach((performer) => {
                      if (performer && performer.employee?.id) {
                        const specialist = buildSpecialistFromPerformer(
                          performer,
                          blockName
                        );
                        if (specialist) {
                          specialists.push(specialist);
                          const specIdx = specialists.length - 1;
                          const effortKey = `${sectionName}-${blockName}-${specIdx}`;
                          restoredEfforts[effortKey] = {
                            day_efforts: performer.day_efforts ?? 0,
                          };
                          console.log(
                            `    ✓ Восстановлен специалист:`,
                            specialist
                          );
                        }
                      }
                    });

                    if (specialists.length > 0) {
                      const key = `${sectionName}-${blockName}`;
                      restoredSpecialists[key] = specialists;
                      console.log(
                        `    ✓ Восстановлено ${specialists.length} специалист(ов) для "${key}"`
                      );
                    }
                  } else {
                    console.log(
                      `    ✗ Нет performers для "${sectionName}-${blockName}"`
                    );
                  }
                });
              } else {
                console.log(
                  `  ✗ Нет block_expenditures для секции "${sectionName}"`
                );
              }
            });
          }

          // Восстанавливаем специалистов для BIM координирования и их трудозатраты
          if (context.financialPlan?.bim_coordination?.roles) {
            const bimSectionName = "BIM координирование";
            context.financialPlan.bim_coordination.roles.forEach((role) => {
              const roleName = role.role;
              const key = `${bimSectionName}-${roleName}`;

              let performers = [];
              if (role.performers && Array.isArray(role.performers)) {
                performers = role.performers;
              } else if (role.performer) {
                performers = [role.performer];
              }

              if (performers.length > 0) {
                const specialists = [];
                performers.forEach((performer) => {
                  if (performer && performer.employee?.id) {
                    const specialist = buildSpecialistFromPerformer(
                      performer,
                      roleName
                    );
                    if (specialist) {
                      const specIdx = specialists.length;
                      specialists.push(specialist);
                      const effortKey = `${bimSectionName}-${roleName}-${specIdx}`;
                      restoredEfforts[effortKey] = {
                        day_efforts: performer.day_efforts ?? 0,
                      };
                    }
                  }
                });

                if (specialists.length > 0) {
                  restoredSpecialists[key] = specialists;
                  console.log(
                    `✓ Восстановлено ${specialists.length} специалист(ов) для "${key}"`
                  );
                }
              }
            });
          }

          // Восстанавливаем специалистов для Контроля качества и их трудозатраты
          if (context.financialPlan?.quality_control?.roles) {
            const qualitySectionName = "Контроль качества";
            context.financialPlan.quality_control.roles.forEach((role) => {
              const roleName = role.role;
              const key = `${qualitySectionName}-${roleName}`;

              let performers = [];
              if (role.performers && Array.isArray(role.performers)) {
                performers = role.performers;
              } else if (role.performer) {
                performers = [role.performer];
              }

              if (performers.length > 0) {
                const specialists = [];
                performers.forEach((performer) => {
                  if (performer && performer.employee?.id) {
                    const specialist = buildSpecialistFromPerformer(
                      performer,
                      roleName
                    );
                    if (specialist) {
                      const specIdx = specialists.length;
                      specialists.push(specialist);
                      const effortKey = `${qualitySectionName}-${roleName}-${specIdx}`;
                      restoredEfforts[effortKey] = {
                        day_efforts: performer.day_efforts ?? 0,
                      };
                    }
                  }
                });

                if (specialists.length > 0) {
                  restoredSpecialists[key] = specialists;
                  console.log(
                    `✓ Восстановлено ${specialists.length} специалист(ов) для "${key}"`
                  );
                }
              }
            });
          }

          // Восстанавливаем специалистов для Ведения проекта и их трудозатраты
          if (context.financialPlan?.project_delivery?.roles) {
            const deliverySectionName = "Ведение проекта";
            context.financialPlan.project_delivery.roles.forEach((role) => {
              const roleName = role.role;
              const key = `${deliverySectionName}-${roleName}`;

              let performers = [];
              if (role.performers && Array.isArray(role.performers)) {
                performers = role.performers;
              } else if (role.performer) {
                performers = [role.performer];
              }

              if (performers.length > 0) {
                const specialists = [];
                performers.forEach((performer) => {
                  if (performer && performer.employee?.id) {
                    const specialist = buildSpecialistFromPerformer(
                      performer,
                      roleName
                    );
                    if (specialist) {
                      const specIdx = specialists.length;
                      specialists.push(specialist);
                      const effortKey = `${deliverySectionName}-${roleName}-${specIdx}`;
                      restoredEfforts[effortKey] = {
                        day_efforts: performer.day_efforts ?? 0,
                      };
                    }
                  }
                });

                if (specialists.length > 0) {
                  restoredSpecialists[key] = specialists;
                  console.log(
                    `✓ Восстановлено ${specialists.length} специалист(ов) для "${key}"`
                  );
                }
              }
            });
          }

          // Восстанавливаем специалистов для Управления проектом и их трудозатраты
          if (context.financialPlan?.project_management?.roles) {
            const managementSectionName = "Управление проектом";
            context.financialPlan.project_management.roles.forEach((role) => {
              const roleName = role.role;
              const key = `${managementSectionName}-${roleName}`;

              let performers = [];
              if (role.performers && Array.isArray(role.performers)) {
                performers = role.performers;
              } else if (role.performer) {
                performers = [role.performer];
              }

              if (performers.length > 0) {
                const specialists = [];
                performers.forEach((performer) => {
                  if (performer && performer.employee?.id) {
                    const specialist = buildSpecialistFromPerformer(
                      performer,
                      roleName
                    );
                    if (specialist) {
                      const specIdx = specialists.length;
                      specialists.push(specialist);
                      const effortKey = `${managementSectionName}-${roleName}-${specIdx}`;
                      restoredEfforts[effortKey] = {
                        day_efforts: performer.day_efforts ?? 0,
                      };
                    }
                  }
                });

                if (specialists.length > 0) {
                  restoredSpecialists[key] = specialists;
                  console.log(
                    `✓ Восстановлено ${specialists.length} специалист(ов) для "${key}"`
                  );
                }
              }
            });
          }

          console.log("=== ИТОГО ===");
          console.log(
            "Всего восстановлено специалистов:",
            Object.keys(restoredSpecialists).length
          );
          console.log("Восстановленные специалисты:", restoredSpecialists);
          setSelectedSpecialists(restoredSpecialists);
          setSpecialistEffortsState((prev) => ({ ...prev, ...restoredEfforts }));
        } else {
          throw new Error("Не удалось преобразовать данные из API");
        }
      } catch (err) {
        setError(err.message);
        console.error("Ошибка загрузки данных финансового плана:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [buildSpecialistFromPerformer]
  );

  // Функция обновления секции
  const updateFinancialPlanSection = useCallback((sectionId, data) => {
    setFinancialPlanData((prev) => {
      const updated = {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionId]: {
            ...prev.sections[sectionId],
            ...data,
          },
        },
      };

      // Также обновляем rawApiData для синхронизации данных
      if (prev.rawApiData) {
        const apiSectionMap = {
          quality_control: "quality_control",
          project_delivery: "project_delivery",
          project_management: "project_management",
          bim_coordination: "bim_coordination",
        };

        const apiSectionKey = apiSectionMap[sectionId];
        if (apiSectionKey && prev.rawApiData[apiSectionKey]) {
          updated.rawApiData = {
            ...prev.rawApiData,
            [apiSectionKey]: {
              ...prev.rawApiData[apiSectionKey],
              // Маппинг полей из sections в rawApiData
              ...(data.quality_percent !== undefined && {
                quality_control_percent: data.quality_percent,
              }),
              ...(data.delivery_percent !== undefined && {
                quality_control_percent: data.delivery_percent,
              }),
              ...(data.management_percent !== undefined && {
                quality_control_percent: data.management_percent,
              }),
              ...(data.bonus_percent !== undefined && {
                direct_bonus_percent: data.bonus_percent,
              }),
              ...(data.include_bonuses !== undefined && {
                include_bonuses: data.include_bonuses,
              }),
            },
          };
        }
      }

      return updated;
    });
  }, []);

  // Функция обновления данных шапки
  const updateHeaderData = useCallback((data) => {
    setFinancialPlanData((prev) => ({
      ...prev,
      headerData: {
        ...prev.headerData,
        ...data,
      },
    }));
  }, []);

  // Вспомогательная функция для получения данных всех секций для калькуляции
  const getCalculationData = useCallback(() => {
    if (!financialPlanData) return null;

    // Дефолтный performer для случаев, когда исполнитель не назначен
    const defaultPerformer = {
      employee: { id: 0, name: "" },
      performer_type: { id: 0, name: "" },
      entity_average_salary: 0,
      entity_name: "",
      day_efforts: 0,
    };

    // Формируем bim_role_salaries из selectedSpecialists для BIM координирования
    // Группируем специалистов по ролям
    const bimRoleSalariesMap = new Map();
    const bimSectionName = "BIM координирование";

    if (selectedSpecialists) {
      Object.keys(selectedSpecialists).forEach((key) => {
        if (key.startsWith(`${bimSectionName}-`)) {
          const roleName = key.replace(`${bimSectionName}-`, "");
          const specialists = selectedSpecialists[key] || [];

          // Группируем всех специалистов для данной роли; day_efforts из specialistEfforts
          if (!bimRoleSalariesMap.has(roleName)) {
            bimRoleSalariesMap.set(roleName, []);
          }

          specialists.forEach((specialist, specIdx) => {
            const effortKey = `${bimSectionName}-${roleName}-${specIdx}`;
            const efforts = specialistEfforts[effortKey];
            const specialistWithEfforts = {
              ...specialist,
              day_efforts: efforts?.day_efforts ?? specialist.day_efforts ?? 0,
            };
            const performer = buildPerformerFromSpecialist(specialistWithEfforts);
            bimRoleSalariesMap.get(roleName).push(performer);
          });
        }
      });
    }

    // Преобразуем Map в массив с массивом performers для каждой роли
    // Если performers пустой, добавляем дефолтный performer
    const bimRoleSalaries = Array.from(bimRoleSalariesMap.entries()).map(
      ([roleName, performers]) => ({
        role: roleName,
        performers: performers.length > 0 ? performers : [defaultPerformer],
      })
    );

    // Если нет специалистов из UI, используем данные из API
    // Проверяем структуру API - может быть performer или performers
    const finalBimRoleSalaries =
      bimRoleSalaries.length > 0
        ? bimRoleSalaries
        : financialPlanData.rawApiData?.bim_coordination?.roles?.map(
            (role) => ({
              role: role.role,
              performers:
                role.performers && role.performers.length > 0
                  ? role.performers
                  : role.performer
                  ? [role.performer]
                  : [defaultPerformer],
            })
          ) || [];

    // Формируем quality_control_role_salaries из selectedSpecialists
    // Группируем специалистов по ролям
    const qualityControlRoleSalariesMap = new Map();
    const qualitySectionName = "Контроль качества";

    if (selectedSpecialists) {
      Object.keys(selectedSpecialists).forEach((key) => {
        if (key.startsWith(`${qualitySectionName}-`)) {
          const roleName = key.replace(`${qualitySectionName}-`, "");
          const specialists = selectedSpecialists[key] || [];

          if (!qualityControlRoleSalariesMap.has(roleName)) {
            qualityControlRoleSalariesMap.set(roleName, []);
          }

          specialists.forEach((specialist, specIdx) => {
            const effortKey = `${qualitySectionName}-${roleName}-${specIdx}`;
            const efforts = specialistEfforts[effortKey];
            const specialistWithEfforts = {
              ...specialist,
              day_efforts: efforts?.day_efforts ?? specialist.day_efforts ?? 0,
            };
            const performer = buildPerformerFromSpecialist(specialistWithEfforts);
            qualityControlRoleSalariesMap.get(roleName).push(performer);
          });
        }
      });
    }

    // Преобразуем Map в массив с массивом performers для каждой роли
    // Если performers пустой, добавляем дефолтный performer
    const qualityControlRoleSalaries = Array.from(
      qualityControlRoleSalariesMap.entries()
    ).map(([roleName, performers]) => ({
      role: roleName,
      performers: performers.length > 0 ? performers : [defaultPerformer],
    }));

    // Если нет специалистов из UI, используем данные из API
    // Проверяем структуру API - может быть performer или performers
    const finalQualityControlRoleSalaries =
      qualityControlRoleSalaries.length > 0
        ? qualityControlRoleSalaries
        : financialPlanData.rawApiData?.quality_control?.roles?.map((role) => ({
            role: role.role,
            performers:
              role.performers && role.performers.length > 0
                ? role.performers
                : role.performer
                ? [role.performer]
                : [defaultPerformer],
          })) || [];

    // Формируем delivery_role_salaries из selectedSpecialists
    // Группируем специалистов по ролям
    const deliveryRoleSalariesMap = new Map();
    const deliverySectionName = "Ведение проекта";

    if (selectedSpecialists) {
      Object.keys(selectedSpecialists).forEach((key) => {
        if (key.startsWith(`${deliverySectionName}-`)) {
          const roleName = key.replace(`${deliverySectionName}-`, "");
          const specialists = selectedSpecialists[key] || [];

          if (!deliveryRoleSalariesMap.has(roleName)) {
            deliveryRoleSalariesMap.set(roleName, []);
          }

          specialists.forEach((specialist, specIdx) => {
            const effortKey = `${deliverySectionName}-${roleName}-${specIdx}`;
            const efforts = specialistEfforts[effortKey];
            const specialistWithEfforts = {
              ...specialist,
              day_efforts: efforts?.day_efforts ?? specialist.day_efforts ?? 0,
            };
            const performer = buildPerformerFromSpecialist(specialistWithEfforts);
            deliveryRoleSalariesMap.get(roleName).push(performer);
          });
        }
      });
    }

    // Преобразуем Map в массив с массивом performers для каждой роли
    // Если performers пустой, добавляем дефолтный performer
    const deliveryRoleSalaries = Array.from(
      deliveryRoleSalariesMap.entries()
    ).map(([roleName, performers]) => ({
      role: roleName,
      performers: performers.length > 0 ? performers : [defaultPerformer],
    }));

    // Если нет специалистов из UI, используем данные из API
    // Проверяем структуру API - может быть performer или performers
    const finalDeliveryRoleSalaries =
      deliveryRoleSalaries.length > 0
        ? deliveryRoleSalaries
        : financialPlanData.rawApiData?.project_delivery?.roles?.map(
            (role) => ({
              role: role.role,
              performers:
                role.performers && role.performers.length > 0
                  ? role.performers
                  : role.performer
                  ? [role.performer]
                  : [defaultPerformer],
            })
          ) || [];

    // Формируем management_role_salaries из selectedSpecialists
    // Группируем специалистов по ролям
    const managementRoleSalariesMap = new Map();
    const managementSectionName = "Управление проектом";

    if (selectedSpecialists) {
      Object.keys(selectedSpecialists).forEach((key) => {
        if (key.startsWith(`${managementSectionName}-`)) {
          const roleName = key.replace(`${managementSectionName}-`, "");
          const specialists = selectedSpecialists[key] || [];

          // Группируем всех специалистов для данной роли
          if (!managementRoleSalariesMap.has(roleName)) {
            managementRoleSalariesMap.set(roleName, {
              performers: [],
              coefficient: 0,
            });
          }

          // Для project_management нужен coefficient, берем из API или используем 0
          const existingRole =
            financialPlanData.rawApiData?.project_management?.roles?.find(
              (r) => r.role === roleName
            );
          const roleData = managementRoleSalariesMap.get(roleName);
          roleData.coefficient = existingRole?.coefficient || 0;

          specialists.forEach((specialist, specIdx) => {
            const effortKey = `${managementSectionName}-${roleName}-${specIdx}`;
            const efforts = specialistEfforts[effortKey];
            const specialistWithEfforts = {
              ...specialist,
              day_efforts: efforts?.day_efforts ?? specialist.day_efforts ?? 0,
            };
            const performer = buildPerformerFromSpecialist(specialistWithEfforts);
            roleData.performers.push(performer);
          });
        }
      });
    }

    // Преобразуем Map в массив с массивом performers для каждой роли
    // Если performers пустой, добавляем дефолтный performer
    const managementRoleSalaries = Array.from(
      managementRoleSalariesMap.entries()
    ).map(([roleName, roleData]) => ({
      role: roleName,
      performers:
        roleData.performers.length > 0
          ? roleData.performers
          : [defaultPerformer],
      coefficient: roleData.coefficient,
    }));

    // Если нет специалистов из UI, используем данные из API
    // Проверяем структуру API - может быть performer или performers
    const finalManagementRoleSalaries =
      managementRoleSalaries.length > 0
        ? managementRoleSalaries
        : financialPlanData.rawApiData?.project_management?.roles?.map(
            (role) => ({
              role: role.role,
              performers:
                role.performers && role.performers.length > 0
                  ? role.performers
                  : role.performer
                  ? [role.performer]
                  : [defaultPerformer],
              coefficient: role.coefficient || 0,
            })
          ) || [];

    return {
      bim_coordination: {
        include_bonuses:
          financialPlanData.rawApiData?.bim_coordination?.include_bonuses !==
          false,
        quality_control_percent:
          financialPlanData.rawApiData?.bim_coordination
            ?.quality_control_percent,
        direct_bonus_percent:
          financialPlanData.rawApiData?.bim_coordination?.direct_bonus_percent,
        bim_role_salaries: finalBimRoleSalaries,
      },
      quality_control: {
        include_bonuses:
          financialPlanData.rawApiData?.quality_control?.include_bonuses !==
          false,
        quality_control_percent:
          financialPlanData.sections?.quality_control?.quality_percent ??
          financialPlanData.rawApiData?.quality_control
            ?.quality_control_percent,
        direct_bonus_percent:
          financialPlanData.sections?.quality_control?.bonus_percent ??
          financialPlanData.rawApiData?.quality_control?.direct_bonus_percent,
        normative_control_efforts:
          financialPlanData.sections?.quality_control
            ?.normative_control_efforts ??
          (financialPlanData.rawApiData?.quality_control?.roles?.find(
            (role) => role.role === "Норм Контроль"
          )?.day_efforts?.direct ||
            0),
        quality_control_role_salaries: finalQualityControlRoleSalaries,
      },
      project_delivery: {
        include_bonuses:
          financialPlanData.rawApiData?.project_delivery?.include_bonuses !==
          false,
        quality_control_percent:
          financialPlanData.sections?.project_delivery?.delivery_percent ??
          financialPlanData.rawApiData?.project_delivery
            ?.quality_control_percent,
        direct_bonus_percent:
          financialPlanData.sections?.project_delivery?.bonus_percent ??
          financialPlanData.rawApiData?.project_delivery?.direct_bonus_percent,
        delivery_role_salaries: finalDeliveryRoleSalaries,
      },
      project_management: {
        include_bonuses:
          financialPlanData.rawApiData?.project_management?.include_bonuses !==
          false,
        quality_control_percent:
          financialPlanData.sections?.project_management?.management_percent ??
          financialPlanData.rawApiData?.project_management
            ?.quality_control_percent,
        direct_bonus_percent:
          financialPlanData.sections?.project_management?.bonus_percent ??
          financialPlanData.rawApiData?.project_management
            ?.direct_bonus_percent,
        administration_efforts:
          financialPlanData.sections?.project_management
            ?.administration_efforts ??
          (financialPlanData.rawApiData?.project_management?.roles?.find(
            (role) => role.role === "Администрирование Проекта"
          )?.day_efforts?.direct ||
            0),
        management_role_salaries: finalManagementRoleSalaries,
      },
    };
  }, [financialPlanData, selectedSpecialists, specialistEfforts, buildPerformerFromSpecialist]);

  // Вспомогательная функция для проверки, является ли раздел единым (без блоков)
  const isUnifiedSection = useCallback((sectionName) => {
    const name = sectionName?.toLowerCase().trim();
    return name === "инсоляция" || name === "гп";
  }, []);

  // Построение полного payload для калькуляции финансового плана
  const buildCalculatePayload = useCallback(
    (overrides = {}) => {
      if (!financialPlanData) return null;

      // Получаем исходные данные API
      const rawApiData = financialPlanData.rawApiData;
      if (!rawApiData) return null;

      // Разделяем разделы на обычные (с блоками) и единые (без блоков)
      const regularSections = (rawApiData.construction_design || []).filter(
        (s) => !isUnifiedSection(s.section?.name)
      );
      const unifiedSections = (rawApiData.construction_design || []).filter(
        (s) => isUnifiedSection(s.section?.name)
      );

      // Строим construction_design с исполнителями только для обычных разделов
      console.log("=== ПОСТРОЕНИЕ PAYLOAD ===");
      console.log("selectedSpecialists:", selectedSpecialists);

      const constructionDesign = regularSections.map((section) => {
        const sectionDetailsData = sectionDetails[section.section.id];
        const sectionName = section.section?.name || "";

        return {
          section_id: section.section.id,
          threshold_percent: section.expenditures_percent?.threshold || 0,
          blocks: (() => {
            const blocks = [];
            (sectionDetailsData?.block_expenditures || []).forEach(
              (block, blockIndex) => {
                const blockName = block.block?.name || `Блок ${blockIndex + 1}`;
                const specialistKey = `${sectionName}-${blockName}`;
                const specialists = selectedSpecialists?.[specialistKey] || [];

                console.log(
                  `Блок: ${sectionName} - ${blockName}, ключ: "${specialistKey}", специалисты:`,
                  specialists
                );

                let performers = [];

                if (specialists.length > 0) {
                  // Обрабатываем всех специалистов для данного блока; day_efforts берём из specialistEfforts
                  specialists.forEach((specialist, specIdx) => {
                    const effortKey = `${sectionName}-${blockName}-${specIdx}`;
                    const efforts = specialistEfforts[effortKey];
                    const specialistWithEfforts = {
                      ...specialist,
                      day_efforts: efforts?.day_efforts ?? specialist.day_efforts ?? 0,
                    };
                    const performer = buildPerformerFromSpecialist(specialistWithEfforts);
                    console.log(
                      `  → Используем specialist:`,
                      specialistWithEfforts,
                      "→ performer:",
                      performer
                    );
                    performers.push(performer);
                  });
                } else {
                  // Если нет специалистов из UI, используем существующие данные или дефолт
                  const defaultPerformer = {
                    employee: { id: 0, name: "" },
                    performer_type: { id: 0, name: "" },
                    entity_average_salary: 0,
                    entity_name: "",
                    day_efforts: 0,
                  };
                  if (
                    block.block?.performers &&
                    Array.isArray(block.block.performers) &&
                    block.block.performers.length > 0
                  ) {
                    // Новая структура API с массивом performers
                    performers = block.block.performers;
                    console.log(
                      `  → Используем существующие performers из API:`,
                      performers
                    );
                  } else if (block.block?.performer) {
                    // Старая структура API с одним performer
                    performers = [block.block.performer];
                    console.log(
                      `  → Используем существующий performer из API (старая структура):`,
                      block.block.performer
                    );
                  } else {
                    // Дефолтный performer, если ничего не выбрано
                    performers = [defaultPerformer];
                    console.log(`  → Используем дефолтный performer`);
                  }
                }

                // day_efforts блока = сумма day_efforts исполнителей (если есть исполнители из UI)
                const blockDayEfforts =
                  performers.length > 0
                    ? performers.reduce((sum, p) => sum + (p.day_efforts || 0), 0)
                    : block.day_efforts || 0;

                const defaultPerformer = {
                  employee: { id: 0, name: "" },
                  performer_type: { id: 0, name: "" },
                  entity_average_salary: 0,
                  entity_name: "",
                  day_efforts: 0,
                };
                blocks.push({
                  tei_id: block.block?.tei_id || 0,
                  day_efforts: blockDayEfforts,
                  performers:
                    performers.length > 0 ? performers : [defaultPerformer],
                });
              }
            );
            return blocks;
          })(),
        };
      });

      // Формируем construction_design_project для единых разделов (без блоков)
      const constructionDesignProject = unifiedSections.map((section) => {
        const sectionDetailsData = sectionDetails[section.section.id];
        const sectionName = section.section?.name?.toLowerCase().trim();
        const isInsolation = sectionName === "инсоляция";

        // Преобразуем performers из данных секции или selectedSpecialists
        const defaultPerformer = {
          employee: { id: 0, name: "" },
          performer_type: { id: 0, name: "" },
          entity_average_salary: 0,
          entity_name: "",
          day_efforts: 0,
        };
        let performers = [];
        const specialistKey = `${section.section?.name || ""}`;
        const specialists = selectedSpecialists?.[specialistKey] || [];

        if (specialists.length > 0) {
          specialists.forEach((specialist) => {
            const performer = buildPerformerFromSpecialist(specialist);
            performers.push(performer);
          });
        } else if (
          section.performers &&
          Array.isArray(section.performers) &&
          section.performers.length > 0
        ) {
          performers = section.performers;
        } else if (section.performer) {
          performers = [section.performer];
        } else {
          performers = [defaultPerformer];
        }

        return {
          section_id: section.section.id,
          threshold_percent: section.expenditures_percent?.threshold || 0,
          // Для "Инсоляции" используем total_day_efforts, для "ГП" - 0 (рассчитывается на бэке)
          day_efforts: isInsolation
            ? sectionDetailsData?.total_day_efforts || 0
            : 0,
          // Гарантируем, что performers не пустой
          performers: performers.length > 0 ? performers : [defaultPerformer],
        };
      });

      console.log(
        "=== ИТОГОВЫЙ constructionDesign ===",
        JSON.stringify(constructionDesign, null, 2)
      );
      console.log(
        "=== ИТОГОВЫЙ constructionDesignProject ===",
        JSON.stringify(constructionDesignProject, null, 2)
      );

      // Получаем глобальный процент бонусов (надежно и предсказуемо)
      const toNumberOrNull = (val) => {
        const n = Number(val);
        return Number.isFinite(n) ? n : null;
      };

      const overridePercent = toNumberOrNull(
        overrides.bonus_percent_construction_design
      );
      const detailsPercent = Object.values(sectionDetails || {})
        .map((det) => toNumberOrNull(det?.bonus_percent))
        .find((val) => val !== null);
      const uiPercent = toNumberOrNull(
        financialPlanData?.sections?.working_design?.bonus_percent
      );
      const apiPercent = toNumberOrNull(
        rawApiData?.bonus_percent_construction_design
      );

      const globalBonusPercent =
        overridePercent ?? detailsPercent ?? uiPercent ?? apiPercent ?? 0;

      // Формируем contract_sections из rawApiData
      // Поддержка новой структуры: contract_sections.sections или старого массива
      let contractSectionsArray = [];
      if (rawApiData.contract_sections) {
        if (
          rawApiData.contract_sections.sections &&
          Array.isArray(rawApiData.contract_sections.sections)
        ) {
          // Новая структура: объект с полем sections
          contractSectionsArray = rawApiData.contract_sections.sections;
        } else if (Array.isArray(rawApiData.contract_sections)) {
          // Старая структура: массив
          contractSectionsArray = rawApiData.contract_sections;
        }
      }

      const contractSections = contractSectionsArray.map((cs) => ({
        section_id: cs.section?.id || cs.section_id,
        amount: cs.amount || 0,
      }));

      const payload = {
        bonus_percent_construction_design: globalBonusPercent,
        construction_design: constructionDesign,
        construction_design_project: constructionDesignProject, // Всегда включаем, даже если массив пустой
        ...getCalculationData(),
      };

      // Добавляем contract_sections, если есть данные
      if (contractSections.length > 0) {
        payload.contract_sections = contractSections;
      }

      // Переопределения (локальные изменения)
      Object.keys(overrides || {}).forEach((k) => {
        if (k !== "bonus_percent_construction_design") {
          payload[k] = { ...(payload[k] || {}), ...(overrides[k] || {}) };
        }
      });

      return payload;
    },
    [
      financialPlanData,
      sectionDetails,
      selectedSpecialists,
      specialistEfforts,
      getCalculationData,
      buildPerformerFromSpecialist,
      isUnifiedSection,
    ]
  );

  const updateSpecialistEfforts = useCallback(
    (sectionName, blockName, specIdx, data) => {
      const key = `${sectionName}-${blockName}-${specIdx}`;
      setSpecialistEffortsState((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), ...data },
      }));
    },
    []
  );

  const getSpecialistEfforts = useCallback((sectionName, blockName, specIdx) => {
    const key = `${sectionName}-${blockName}-${specIdx}`;
    return specialistEfforts[key];
  }, [specialistEfforts]);

  // Функция сохранения
  const saveFinancialPlan = useCallback(
    async (projectId) => {
      if (!financialPlanData) {
        console.error("Нет данных финансового плана для сохранения");
        return { success: false, error: "Нет данных для сохранения" };
      }

      setIsLoading(true);
      setError(null);

      try {
        // Строим payload для сохранения (аналогично калькуляции)
        const payload = buildCalculatePayload();
        if (!payload) {
          throw new Error("Не удалось построить payload для сохранения");
        }

        // Сохраняем данные через API
        const response = await fetch(
          `${getAPIURL()}fin_plan/projects/${projectId}/save`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const formatted = (() => {
            if (!errorData) return `HTTP error! status: ${response.status}`;
            if (typeof errorData === "string") return errorData;
            if (Array.isArray(errorData))
              return errorData.map((e) => JSON.stringify(e)).join("; ");
            if (typeof errorData.detail === "string") return errorData.detail;
            if (Array.isArray(errorData.detail))
              return errorData.detail.map((e) => JSON.stringify(e)).join("; ");
            return JSON.stringify(errorData);
          })();
          throw new Error(formatted);
        }

        const result = await response.json();

        console.log("=== ОТВЕТ СЕРВЕРА ПОСЛЕ СОХРАНЕНИЯ ===");
        console.log("Результат:", result);

        return { success: true, data: result };
      } catch (err) {
        setError(err.message);
        console.error("Ошибка сохранения финансового плана:", err);
        return { success: false, error: err.message };
      } finally {
        setIsLoading(false);
      }
    },
    [financialPlanData, buildCalculatePayload]
  );

  // Функция отправки на согласование
  const submitForApproval = useCallback(async (projectId, approvalData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Отправляем на согласование через реальный API
      const result = await submitFinancialPlanForApproval(
        projectId,
        approvalData
      );

      return { success: true, data: result };
    } catch (err) {
      setError(err.message);
      console.error("Ошибка отправки на согласование:", err);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Функция калькуляции с обновлением контекста (аналогично EstimateContext)
  const performCalculation = useCallback(
    async (projectId, overrides = {}) => {
      if (!financialPlanData) {
        console.error("Нет данных финансового плана для калькуляции");
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Строим payload для калькуляции
        const payload = buildCalculatePayload(overrides);
        if (!payload) {
          throw new Error("Не удалось построить payload для калькуляции");
        }

        // Выполняем калькуляцию через API
        const response = await fetch(
          `${getAPIURL()}fin_plan/projects/${projectId}/calculate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const formatted = (() => {
            if (!errorData) return `HTTP error! status: ${response.status}`;
            if (typeof errorData === "string") return errorData;
            if (Array.isArray(errorData))
              return errorData.map((e) => JSON.stringify(e)).join("; ");
            if (typeof errorData.detail === "string") return errorData.detail;
            if (Array.isArray(errorData.detail))
              return errorData.detail.map((e) => JSON.stringify(e)).join("; ");
            return JSON.stringify(errorData);
          })();
          throw new Error(formatted);
        }

        const result = await response.json();

        // Обновляем контекст с результатами калькуляции
        const updatedContext = updateFinancialPlanContextWithCalculationResults(
          {
            financialPlan: financialPlanData.rawApiData || result,
            sections: sectionDetails,
          },
          result
        );

        // Обновляем данные, сохраняя UI состояние
        setFinancialPlanData((prevData) => {
          const newData = {
            ...prevData,
            rawApiData: updatedContext.financialPlan,
            sections: {
              ...prevData.sections,
              working_design: {
                ...prevData.sections.working_design,
                sections:
                  updatedContext.financialPlan.construction_design?.map(
                    (section) => ({
                      id: section.section.id,
                      name: section.section.name,
                      labor_days: section.day_efforts.direct,
                      bonus_days: section.day_efforts.bonus,
                      direct_costs: section.money_expenditures.direct,
                      bonus_costs: section.money_expenditures.bonus,
                      total_costs: section.price_with_payroll_taxes,
                      payroll_taxes: section.payroll_taxes,
                      base_price_per_m2: section.base_price_per_m2,
                      expenditures_percent: section.expenditures_percent,
                      different_in_expenditures:
                        section.different_in_expenditures,
                    })
                  ) || [],
                total_costs:
                  updatedContext.financialPlan.total_construction_design
                    ?.price_with_payroll_taxes ??
                  prevData.sections.working_design.total_costs,
                direct_costs:
                  updatedContext.financialPlan.total_construction_design
                    ?.money_expenditures?.direct ??
                  prevData.sections.working_design.direct_costs,
                bonus_fund:
                  updatedContext.financialPlan.total_construction_design
                    ?.money_expenditures?.bonus ??
                  prevData.sections.working_design.bonus_fund,
                bonus_percent:
                  updatedContext.financialPlan
                    .bonus_percent_construction_design ??
                  prevData.sections.working_design.bonus_percent,
              },
              quality_control: {
                ...prevData.sections.quality_control,
                sections:
                  updatedContext.financialPlan.quality_control?.roles?.map(
                    (role) => ({
                      id: role.role,
                      name: role.role,
                      labor_days: role.day_efforts.direct,
                      bonus_days: role.day_efforts.bonus,
                      direct_costs: role.money_expenditures.direct,
                      bonus_costs: role.money_expenditures.bonus,
                      performer: role.performer,
                    })
                  ) || prevData.sections.quality_control.sections,
                total_costs:
                  updatedContext.financialPlan.quality_control?.total ??
                  prevData.sections.quality_control.total_costs,
                direct_costs:
                  updatedContext.financialPlan.quality_control
                    ?.total_money_expenditures?.direct ??
                  prevData.sections.quality_control.direct_costs,
                bonus_fund:
                  updatedContext.financialPlan.quality_control
                    ?.total_money_expenditures?.bonus ??
                  prevData.sections.quality_control.bonus_fund,
                normative_control_efforts:
                  updatedContext.financialPlan.quality_control?.roles?.find(
                    (role) => role.role === "Норм Контроль"
                  )?.day_efforts?.direct ??
                  prevData.sections.quality_control.normative_control_efforts,
              },
              project_management: {
                ...prevData.sections.project_management,
                sections:
                  updatedContext.financialPlan.project_management?.roles?.map(
                    (role) => ({
                      id: role.role,
                      name: role.role,
                      labor_days: role.day_efforts.direct,
                      bonus_days: role.day_efforts.bonus,
                      direct_costs: role.money_expenditures.direct,
                      bonus_costs: role.money_expenditures.bonus,
                      performer: role.performer,
                      coefficient: role.coefficient,
                    })
                  ) || prevData.sections.project_management.sections,
                total_costs:
                  updatedContext.financialPlan.project_management?.total ??
                  prevData.sections.project_management.total_costs,
                direct_costs:
                  updatedContext.financialPlan.project_management
                    ?.total_money_expenditures?.direct ??
                  prevData.sections.project_management.direct_costs,
                bonus_fund:
                  updatedContext.financialPlan.project_management
                    ?.total_money_expenditures?.bonus ??
                  prevData.sections.project_management.bonus_fund,
                administration_efforts:
                  updatedContext.financialPlan.project_management?.roles?.find(
                    (role) => role.role === "Администрирование Проекта"
                  )?.day_efforts?.direct ??
                  prevData.sections.project_management.administration_efforts,
              },
              bim_coordination: {
                ...prevData.sections.bim_coordination,
                sections:
                  updatedContext.financialPlan.bim_coordination?.roles?.map(
                    (role) => ({
                      id: role.role,
                      name: role.role,
                      labor_days: role.day_efforts.direct,
                      bonus_days: role.day_efforts.bonus,
                      direct_costs: role.money_expenditures.direct,
                      bonus_costs: role.money_expenditures.bonus,
                      performer: role.performer,
                    })
                  ) || prevData.sections.bim_coordination.sections,
                total_costs:
                  updatedContext.financialPlan.bim_coordination?.total ??
                  prevData.sections.bim_coordination.total_costs,
                direct_costs:
                  updatedContext.financialPlan.bim_coordination
                    ?.total_money_expenditures?.direct ??
                  prevData.sections.bim_coordination.direct_costs,
                bonus_fund:
                  updatedContext.financialPlan.bim_coordination
                    ?.total_money_expenditures?.bonus ??
                  prevData.sections.bim_coordination.bonus_fund,
              },
              project_delivery: {
                ...prevData.sections.project_delivery,
                sections:
                  updatedContext.financialPlan.project_delivery?.roles?.map(
                    (role) => ({
                      id: role.role,
                      name: role.role,
                      labor_days: role.day_efforts.direct,
                      bonus_days: role.day_efforts.bonus,
                      direct_costs: role.money_expenditures.direct,
                      bonus_costs: role.money_expenditures.bonus,
                      performer: role.performer,
                    })
                  ) || prevData.sections.project_delivery.sections,
                total_costs:
                  updatedContext.financialPlan.project_delivery?.total ??
                  prevData.sections.project_delivery.total_costs,
                direct_costs:
                  updatedContext.financialPlan.project_delivery
                    ?.total_money_expenditures?.direct ??
                  prevData.sections.project_delivery.direct_costs,
                bonus_fund:
                  updatedContext.financialPlan.project_delivery
                    ?.total_money_expenditures?.bonus ??
                  prevData.sections.project_delivery.bonus_fund,
              },
            },
            headerData: {
              ...prevData.headerData,
              contract_value:
                updatedContext.financialPlan.contract_value ||
                prevData.headerData.contract_value,
              contract_value_before_nds:
                updatedContext.financialPlan.contract_value_before_nds ||
                prevData.headerData.contract_value_before_nds,
              contract_value_construction:
                updatedContext.financialPlan.contract_value_construction ||
                prevData.headerData.contract_value_construction,
              cost_price:
                updatedContext.financialPlan.cost_price ||
                prevData.headerData.cost_price,
              difference:
                updatedContext.financialPlan.difference ||
                prevData.headerData.difference,
              total_area:
                updatedContext.financialPlan.total_area ||
                prevData.headerData.total_area,
              nds_value:
                updatedContext.financialPlan.nds_value ||
                prevData.headerData.nds_value,
              indirect_expenditures:
                updatedContext.financialPlan.indirect_expenditures ||
                prevData.headerData.indirect_expenditures,
              indirect_expenditures_percent:
                updatedContext.financialPlan.indirect_expenditures_percent ||
                prevData.headerData.indirect_expenditures_percent,
              cost_price_percent:
                updatedContext.financialPlan.cost_price_percent ||
                prevData.headerData.cost_price_percent,
              pure_income:
                updatedContext.financialPlan.pure_income ||
                prevData.headerData.pure_income,
              pure_income_percent:
                updatedContext.financialPlan.pure_income_percent ||
                prevData.headerData.pure_income_percent,
            },
          };
          return newData;
        });
        setSectionDetails(updatedContext.sections);

        return result;
      } catch (err) {
        console.error("Ошибка калькуляции:", err);
        setError(err.message || "Ошибка калькуляции");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [financialPlanData, sectionDetails, buildCalculatePayload]
  );

  // Функция расчета финансового плана (для обратной совместимости)
  const calculateFinancialPlanData = useCallback(async (projectId, data) => {
    setIsLoading(true);
    setError(null);

    try {
      // Рассчитываем финансовый план через реальный API
      const result = await calculateFinancialPlan(projectId, data);

      // Обновляем данные результатами расчета
      if (result) {
        const transformedData = transformApiDataToUIFormat(result);
        if (transformedData) {
          setFinancialPlanData(transformedData);
        }
      }

      return { success: true, data: result };
    } catch (err) {
      setError(err.message);
      console.error("Ошибка расчета финансового плана:", err);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Данные секции уже приходят в основном запросе по проекту; отдельный запрос не делаем.
  const loadSectionDetails = useCallback(
    async (projectId, sectionId) => {
      const existing = sectionDetails[sectionId];
      if (existing) return existing;
      return (
        sectionDetails[sectionId] ?? {
          block_expenditures: [],
          bonus_percent: 0,
          threshold_percent: 0,
          total_day_efforts: 0,
        }
      );
    },
    [sectionDetails]
  );

  // Функция калькуляции для конкретной секции
  const performSectionCalculation = useCallback(
    async (projectId, sectionId, sectionData) => {
      if (!financialPlanData) {
        console.error("Нет данных финансового плана для калькуляции");
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Получаем исходные данные API для калькуляции
        const rawApiData =
          financialPlanData.rawApiData ||
          (await getFinancialPlanData(projectId));

        const result = await calculateFinancialPlanSection(
          {
            financialPlan: rawApiData,
            sections: sectionDetails,
          },
          projectId,
          sectionId,
          sectionData
        );

        // Обновляем контекст с результатами калькуляции
        const updatedContext = updateFinancialPlanContextWithCalculationResults(
          {
            financialPlan: rawApiData,
            sections: sectionDetails,
          },
          result
        );

        // Обновляем данные, сохраняя UI состояние
        setFinancialPlanData((prevData) => {
          const newData = {
            ...prevData,
            rawApiData: updatedContext.financialPlan,
            sections: {
              ...prevData.sections,
              working_design: {
                ...prevData.sections.working_design,
                sections:
                  updatedContext.financialPlan.construction_design?.map(
                    (section) => ({
                      id: section.section.id,
                      name: section.section.name,
                      labor_days: section.day_efforts.direct,
                      bonus_days: section.day_efforts.bonus,
                      direct_costs: section.money_expenditures.direct,
                      bonus_costs: section.money_expenditures.bonus,
                      total_costs: section.price_with_payroll_taxes,
                      payroll_taxes: section.payroll_taxes,
                      base_price_per_m2: section.base_price_per_m2,
                      expenditures_percent: section.expenditures_percent,
                      different_in_expenditures:
                        section.different_in_expenditures,
                    })
                  ) || [],
                total_costs:
                  updatedContext.financialPlan.total_construction_design
                    ?.price_with_payroll_taxes ??
                  prevData.sections.working_design.total_costs,
                direct_costs:
                  updatedContext.financialPlan.total_construction_design
                    ?.money_expenditures?.direct ??
                  prevData.sections.working_design.direct_costs,
                bonus_fund:
                  updatedContext.financialPlan.total_construction_design
                    ?.money_expenditures?.bonus ??
                  prevData.sections.working_design.bonus_fund,
                bonus_percent:
                  updatedContext.financialPlan
                    .bonus_percent_construction_design ??
                  prevData.sections.working_design.bonus_percent,
              },
            },
            headerData: {
              ...prevData.headerData,
              contract_value:
                updatedContext.financialPlan.contract_value ||
                prevData.headerData.contract_value,
              contract_value_before_nds:
                updatedContext.financialPlan.contract_value_before_nds ||
                prevData.headerData.contract_value_before_nds,
              contract_value_construction:
                updatedContext.financialPlan.contract_value_construction ||
                prevData.headerData.contract_value_construction,
              cost_price:
                updatedContext.financialPlan.cost_price ||
                prevData.headerData.cost_price,
              difference:
                updatedContext.financialPlan.difference ||
                prevData.headerData.difference,
              total_area:
                updatedContext.financialPlan.total_area ||
                prevData.headerData.total_area,
              nds_value:
                updatedContext.financialPlan.nds_value ||
                prevData.headerData.nds_value,
              indirect_expenditures:
                updatedContext.financialPlan.indirect_expenditures ||
                prevData.headerData.indirect_expenditures,
              indirect_expenditures_percent:
                updatedContext.financialPlan.indirect_expenditures_percent ||
                prevData.headerData.indirect_expenditures_percent,
              cost_price_percent:
                updatedContext.financialPlan.cost_price_percent ||
                prevData.headerData.cost_price_percent,
              pure_income:
                updatedContext.financialPlan.pure_income ||
                prevData.headerData.pure_income,
              pure_income_percent:
                updatedContext.financialPlan.pure_income_percent ||
                prevData.headerData.pure_income_percent,
            },
          };
          return newData;
        });
        setSectionDetails(updatedContext.sections);

        return result;
      } catch (err) {
        console.error("Ошибка калькуляции секции:", err);
        setError(err.message || "Ошибка калькуляции секции");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [financialPlanData, sectionDetails]
  );

  // Функции для работы с выбранными исполнителями
  const addSpecialist = useCallback(
    (sectionName, blockName, specialistData) => {
      const key = `${sectionName}-${blockName}`;
      setSelectedSpecialists((prev) => {
        const currentSpecialists = prev[key] || [];
        return {
          ...prev,
          [key]: [...currentSpecialists, specialistData],
        };
      });
    },
    []
  );

  const removeSpecialist = useCallback(
    (sectionName, blockName, specialistIndex) => {
      const key = `${sectionName}-${blockName}`;
      setSelectedSpecialists((prev) => {
        const currentSpecialists = prev[key] || [];
        const updatedSpecialists = currentSpecialists.filter(
          (_, index) => index !== specialistIndex
        );

        if (updatedSpecialists.length === 0) {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        }

        return {
          ...prev,
          [key]: updatedSpecialists,
        };
      });
    },
    []
  );

  const updateContractSections = useCallback((sections) => {
    const totalAmount = (sections || []).reduce(
      (sum, s) => sum + (parseFloat(s.amount) || 0),
      0
    );
    setFinancialPlanData((prev) => ({
      ...prev,
      rawApiData: {
        ...(prev.rawApiData || {}),
        contract_sections: {
          sections: sections || [],
          total_amount: totalAmount,
        },
      },
    }));
  }, []);

  const contextValue = {
    // Состояние
    financialPlanData,
    sectionDetails,
    selectedSpecialists,
    performerTypes,
    isLoading,
    error,

    // Функции
    loadFinancialPlanData,
    updateFinancialPlanSection,
    updateHeaderData,
    saveFinancialPlan,
    submitForApproval,
    calculateFinancialPlanData,
    performCalculation,
    performSectionCalculation,
    loadSectionDetails,
    addSpecialist,
    removeSpecialist,
    setSectionDetails,
    updateSpecialistEfforts,
    getSpecialistEfforts,
    specialistEfforts,
    updateContractSections,

    // Новые функции для калькуляции
    getCalculationData,
    buildCalculatePayload,
    buildPerformerFromSpecialist,
    buildSpecialistFromPerformer,
  };

  return (
    <FinancialPlanStateContext.Provider value={contextValue}>
      {children}
    </FinancialPlanStateContext.Provider>
  );
};
